import { ChainId } from '../../web3.types'

/** RPC endpoint kinds we track gas-cap floors for. */
export type EndpointKind = 'alchemy' | 'erpc'

export const DEFAULT_ETH_CALL_GAS_CAP = 50_000_000

/**
 * Per-(chainId, endpointKind) `eth_call` gas-limit floor — the largest `gas` parameter
 * the endpoint accepts when the request is replicated enough times to hit every upstream
 * an aggregator (e.g. eRPC) might delegate to. Values are floored to 2 significant
 * figures so transports can pass them through directly without further rounding.
 *
 * These came from a saturation probe against each endpoint. viem-dlc now reports the figure it
 * actually observed as `gas_limit_observed` on its wide event, which is where a refresh should
 * come from.
 */
const RPC_GAS_LIMITS: Partial<Record<ChainId, Partial<Record<EndpointKind, number>>>> = {
  [ChainId.Mainnet]: { alchemy: 550_000_000, erpc: 60_000_000 },
  [ChainId.Optimism]: { alchemy: 550_000_000, erpc: 51_000_000 },
  [ChainId.Unichain]: { alchemy: 550_000_000, erpc: 550_000_000 },
  [ChainId.Polygon]: { alchemy: 550_000_000, erpc: 550_000_000 },
  [ChainId.Monad]: { alchemy: 150_000_000, erpc: 150_000_000 },
  [ChainId.Cronos]: { alchemy: 25_000_000, erpc: 200_000 },
  [ChainId.Worldchain]: { alchemy: 550_000_000, erpc: 50_000_000 },
  [ChainId.Stable]: { alchemy: 25_000_000, erpc: 25_000_000 },
  [ChainId.Hyperevm]: { alchemy: 30_000_000, erpc: 30_000_000 },
  [ChainId.Base]: { alchemy: 550_000_000, erpc: 550_000_000 },
  [ChainId.Arbitrum]: { alchemy: 990_000_000, erpc: 600_000_000 },
  [ChainId.Plume]: { erpc: 110_000_000 },
  [ChainId.Katana]: { alchemy: 550_000_000, erpc: 830_000_000 },
  [ChainId.Robinhood]: { alchemy: 550_000_000, erpc: 550_000_000 },
  [ChainId.Tempo]: { alchemy: 550_000_000, erpc: 550_000_000 },
  [ChainId.Arc]: { alchemy: 550_000_000, erpc: 550_000_000 }
}

/**
 * Look up the gas-cap floor for `(chainId, endpointKind)`. Returns `undefined` when no
 * value has been measured yet — callers should fall back to a chain-agnostic conservative
 * default (typical: 50_000_000 for Alchemy-class endpoints).
 */
function getEthCallGasCap(chainId: ChainId, endpointKind: EndpointKind): number | undefined {
  return RPC_GAS_LIMITS[chainId]?.[endpointKind]
}

function getRpcEndpointKind(rpcUrl: string): EndpointKind | undefined {
  const url = rpcUrl.toLowerCase()
  if (url.includes('alchemy.com')) return 'alchemy'
  if (url.includes('rpc.morpho.dev')) return 'erpc'
}

export function getEthCallGasCapForEndpoint(
  chainId: ChainId,
  rpcUrl: string,
  fallback = DEFAULT_ETH_CALL_GAS_CAP
) {
  const endpointKind = getRpcEndpointKind(rpcUrl)
  return endpointKind ? (getEthCallGasCap(chainId, endpointKind) ?? fallback) : fallback
}
